import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Upload, X, FileText, File as FileIcon, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  useDocuments 
} from "@/hooks/useDocuments";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";

const documentFormSchema = z.object({
  title: z.string().min(1, "Document title is required"),
  description: z.string().optional(),
  organization_id: z.string().optional(),
  contact_id: z.string().optional(),
  opportunity_id: z.string().optional(),
  is_template: z.boolean().default(false),
  status: z.string().default("active"),
});

type DocumentFormValues = z.infer<typeof documentFormSchema>;

const FILE_SIZE_LIMIT = 10 * 1024 * 1024; // 10MB
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'image/jpeg',
  'image/png',
  'image/gif',
  'text/plain',
  'application/rtf'
];

export function DocumentUploadForm() {
  const navigate = useNavigate();
  const { createDocument } = useDocuments();
  const { organizations } = useOrganizations();
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const form = useForm<DocumentFormValues>({
    resolver: zodResolver(documentFormSchema),
    defaultValues: {
      title: "",
      description: "",
      status: "active",
      is_template: false,
    },
  });

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const validateFile = (file: File): boolean => {
    setFileError(null);
    
    if (file.size > FILE_SIZE_LIMIT) {
      setFileError(`File size exceeds ${FILE_SIZE_LIMIT / (1024 * 1024)}MB limit`);
      return false;
    }
    
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setFileError("File type not supported");
      return false;
    }
    
    return true;
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      
      if (validateFile(droppedFile)) {
        setFile(droppedFile);
        // Set title to file name automatically if not already set
        if (!form.getValues("title")) {
          const fileName = droppedFile.name.split('.').slice(0, -1).join('.');
          form.setValue("title", fileName);
        }
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      
      if (validateFile(selectedFile)) {
        setFile(selectedFile);
        // Set title to file name automatically if not already set
        if (!form.getValues("title")) {
          const fileName = selectedFile.name.split('.').slice(0, -1).join('.');
          form.setValue("title", fileName);
        }
      }
    }
  };

  const removeFile = () => {
    setFile(null);
    setFileError(null);
  };

  const onSubmit = async (values: DocumentFormValues) => {
    if (!file) {
      toast({
        title: "No file selected",
        description: "Please select a file to upload",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const documentData = {
        title: values.title,
        description: values.description,
        file_type: file.type,
        file_size: file.size,
        file_path: "", // Will be set by the service
        organization_id: values.organization_id,
        contact_id: values.contact_id,
        opportunity_id: values.opportunity_id,
        is_template: values.is_template,
        status: values.status
      };

      await createDocument({ documentData, file });
      toast({
        title: "Document uploaded",
        description: "Your document has been successfully uploaded"
      });
      navigate('/documents');
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message || "There was an error uploading your document",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFileIcon = (file: File) => {
    if (file.type.includes('pdf')) {
      return <FileText className="h-10 w-10 text-red-500" />;
    } else if (file.type.includes('image')) {
      return <FileText className="h-10 w-10 text-blue-500" />;
    } else if (file.type.includes('word') || file.type.includes('doc')) {
      return <FileText className="h-10 w-10 text-indigo-500" />;
    } else if (file.type.includes('excel') || file.type.includes('sheet')) {
      return <FileText className="h-10 w-10 text-green-500" />;
    } else {
      return <FileIcon className="h-10 w-10 text-gray-500" />;
    }
  };

  const formatFileSize = (sizeInBytes: number): string => {
    if (sizeInBytes < 1024) {
      return `${sizeInBytes} B`;
    } else if (sizeInBytes < 1024 * 1024) {
      return `${(sizeInBytes / 1024).toFixed(2)} KB`;
    } else if (sizeInBytes < 1024 * 1024 * 1024) {
      return `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB`;
    } else {
      return `${(sizeInBytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Document Title*</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter document title" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="mt-4">
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Enter a description for this document" 
                      className="resize-none"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="organization_id"
              render={({ field }) => (
                <FormItem className="mt-4">
                  <FormLabel>Related Organization</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an organization" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="">None</SelectItem>
                      {organizations.map((org) => (
                        <SelectItem key={org.id} value={org.id}>
                          {org.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Associate this document with an organization
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem className="mt-4">
                  <FormLabel>Status</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_template"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 mt-4">
                  <FormControl>
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <div className="flex items-center space-x-2">
                      <FormLabel>Save as template</FormLabel>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="w-80">
                            <p>Templates are reusable documents that can be shared across your organization.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <FormDescription>
                      Make this document available as a template for future use
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
          </div>
          
          <div className="space-y-4">
            <div 
              className={`
                border-2 border-dashed rounded-md p-6 transition-colors
                ${dragActive ? 'border-primary bg-primary/5' : 'border-gray-300'} 
                ${file ? 'bg-gray-50' : 'bg-white'}
                ${fileError ? 'border-red-500 bg-red-50' : ''}
              `}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {file ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getFileIcon(file)}
                    <div>
                      <p className="font-medium">{file.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatFileSize(file.size)}
                      </p>
                      <Badge variant="secondary" className="mt-1">{file.type}</Badge>
                    </div>
                  </div>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    onClick={removeFile}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-4 flex text-sm leading-6 text-gray-600">
                    <label
                      htmlFor="file-upload"
                      className="relative cursor-pointer rounded-md font-semibold text-primary focus-within:outline-none focus-within:ring-2 focus-within:ring-primary"
                    >
                      <span>Click to upload a file</span>
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        className="sr-only"
                        onChange={handleFileChange}
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    PDF, Word, Excel, PowerPoint, and image files up to 10MB
                  </p>
                  {fileError && (
                    <p className="text-sm text-red-500 mt-2">{fileError}</p>
                  )}
                </div>
              )}
            </div>

            {file && file.type.includes('image') && (
              <div className="bg-gray-50 p-4 rounded-md">
                <h3 className="text-sm font-semibold">Document Preview</h3>
                <div className="mt-2">
                  <div className="max-h-72 overflow-hidden">
                    <img 
                      src={URL.createObjectURL(file)} 
                      alt="Preview" 
                      className="max-w-full rounded-md"
                    />
                  </div>
                </div>
              </div>
            )}

            {file && !file.type.includes('image') && (
              <div className="bg-gray-50 p-4 rounded-md">
                <h3 className="text-sm font-semibold">Document Information</h3>
                <div className="mt-2 p-4 border rounded-md bg-white">
                  {getFileIcon(file)}
                  <p className="mt-2 text-sm font-medium">{file.name}</p>
                  <div className="mt-1 flex flex-wrap gap-2">
                    <Badge variant="outline">{formatFileSize(file.size)}</Badge>
                    <Badge variant="outline">{file.type.split('/')[1].toUpperCase()}</Badge>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-2 pt-4">
          <Button 
            type="button" 
            variant="outline"
            onClick={() => navigate('/documents')}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={!file || isSubmitting || !!fileError}
          >
            {isSubmitting ? "Uploading..." : "Upload Document"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
